import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

const sections = [
  { id: 'hero', label: 'Home', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  )},
  { id: 'about', label: 'About', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  )},
  { id: 'skills', label: 'Skills', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
    </svg>
  )},
  { id: 'projects', label: 'Work', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
    </svg>
  )},
  { id: 'contact', label: 'Contact', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
    </svg>
  )},
];

const MobileNav = () => {
  const [active, setActive] = useState('hero');
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [circleLeft, setCircleLeft] = useState(0);

  // Calculate circle position from actual DOM measurements
  const updateCirclePosition = useCallback((index: number) => {
    const container = containerRef.current;
    const btn = buttonRefs.current[index];
    if (!container || !btn) return;
    const containerRect = container.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const centerX = btnRect.left - containerRect.left + btnRect.width / 2 - 24; // 24 = half of 48px circle
    setCircleLeft(centerX);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + window.innerHeight / 2;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i].id);
        if (el && el.offsetTop <= scrollY) {
          setActive(sections[i].id);
          updateCirclePosition(i);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initial position after mount
    requestAnimationFrame(() => {
      const idx = sections.findIndex(s => s.id === 'hero');
      updateCirclePosition(idx >= 0 ? idx : 0);
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [updateCirclePosition]);

  // Recalculate on resize
  useEffect(() => {
    const handleResize = () => {
      const idx = sections.findIndex(s => s.id === active);
      updateCirclePosition(idx >= 0 ? idx : 0);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [active, updateCirclePosition]);

  const handleClick = (id: string, index: number) => {
    setActive(id);
    updateCirclePosition(index);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
      <div
        className="mx-3 mb-3 rounded-2xl border border-border/40 overflow-visible"
        style={{
          background: 'hsl(var(--background) / 0.75)',
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
          boxShadow: '0 -4px 30px hsl(0 0% 0% / 0.15)',
          border: '1px solid hsl(var(--border) / 0.4)',
        }}
      >
        <div ref={containerRef} className="flex items-center justify-around px-2 py-2 relative">
          {/* Floating circle indicator — positioned by actual DOM measurement */}
          <motion.div
            className="absolute rounded-full"
            style={{
              width: 48,
              height: 48,
              background: 'hsl(var(--primary))',
              boxShadow: '0 4px 20px hsl(var(--primary) / 0.4), 0 0 40px hsl(var(--primary) / 0.15)',
              top: -12,
            }}
            animate={{ left: circleLeft }}
            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
          />

          {sections.map((section, index) => {
            const isActive = active === section.id;
            return (
              <button
                key={section.id}
                ref={(el) => { buttonRefs.current[index] = el; }}
                onClick={() => handleClick(section.id, index)}
                className="relative flex flex-col items-center outline-none z-10"
                style={{ flex: 1 }}
              >
                <motion.div
                  animate={{
                    y: isActive ? -14 : 0,
                    scale: isActive ? 1.05 : 0.9,
                  }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  className={`relative flex items-center justify-center p-2.5 transition-colors duration-200 ${
                    isActive ? 'text-primary-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {section.icon}
                </motion.div>

                {/* Label — only for active */}
                <motion.span
                  animate={{
                    opacity: isActive ? 1 : 0,
                    y: isActive ? -2 : 6,
                  }}
                  transition={{ duration: 0.2 }}
                  className="text-[9px] font-mono font-semibold text-primary tracking-wider"
                >
                  {section.label}
                </motion.span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default MobileNav;
